// ink-component
import React, { InputHTMLAttributes, ReactNode, RefObject } from 'react';

import CoreCheckbox from '../_common/CoreCheckbox/CoreCheckbox';

import { ControllerProps } from '../_common/Controller/Controller';

export type NewCheckboxProps = Omit<ControllerProps, 'render' | 'theme'> &
  InputHTMLAttributes<HTMLInputElement> & {
    /** Optional text label on the right of the checkbox */
    label?: ReactNode;
    /** Size modifier */
    width?: 'large' | 'half' | 'quarter' | 'full';
    /** Input checked attribute */
    checked?: boolean;
    /** Modifier that highlights the checkbox as invalid */
    invalid?: boolean;
    /** Inline modifier */
    inline?: boolean;
    /** Add indeterminate state to checkbox */
    indeterminate?: boolean;
    /** React ref object to be forwarded to the internal node */
    externalRef?: RefObject<HTMLInputElement>;
    /** Allows screen readers to describe the content. The correct use of Field excludes the need for aria-label */
    'aria-label'?: string;
    /** Marks the element with a given ID to make it easier to select in tests */
    'data-testid'?: string | number;
    /** Unique name for analytics purposes. By default, components are not tracked, unless this is set */
    'data-track-name'?: string;
    /** List of tracked events in the analytics integration */
    'data-track-events'?: string[];
    /** Custom payload to be sent to analytics provider */
    'data-track-payload'?: any;
    /** Trims top spacing (default) and removes width restriction */
    trim?: boolean;
  };

/**
 * @purpose Allow users to choose options from a short, visible, set
 * @categories form
 * @aka checkbox
 * @HTMLType HTMLInputElement
 */
function NewCheckbox(props: NewCheckboxProps) {
  const { className, style, ...otherProps } = props;
  return <CoreCheckbox {...otherProps} />;
}

NewCheckbox.defaultProps = {
  indeterminate: false,
  label: undefined,
  invalid: false,
  width: 'large',
  inline: false,
  'data-track-events': ['change'],
  variant: 'default',
  trim: false,
};

NewCheckbox.displayName = 'NewCheckbox';

export default NewCheckbox;
